import type { GateContext, Lesson } from '../types';
import { f, findPair, peak, run } from './helpers';

const H = 0.05;
const bandRun = 'm3-h05';

/** A moment in the hysteresis run where the error is inside the band. */
const insideBand = (ctx: GateContext) =>
  findPair(ctx, bandRun, (s) => Math.abs(s.e) < H * 0.8, 0.5) ?? findPair(ctx, bandRun, () => true)!;
/** A moment where the error is below −h. */
const belowBand = (ctx: GateContext) =>
  findPair(ctx, bandRun, (s, p) => s.e < -H - 0.005 && p.sw === 1) ?? findPair(ctx, bandRun, (s) => s.e < -H - 0.005)!;

const level = (sw: number) => (sw === 1 ? 'high' : 'low');
const nextLevel = (prevSw: number, e: number, h: number) => (e > h ? 'high' : e < -h ? 'low' : level(prevSw));

export const m3: Lesson = {
  id: 'm3',
  number: 3,
  title: 'Bang-bang control',
  summary: 'In this module, you will explore the simplest feedback rule: full throttle when you are too low, and cut power when you are too high. See why this causes endless chatter, and how a switching band helps.',
  objectives: [
    'Apply the bang-bang switching rule with hysteresis.',
    'Identify overshoot and explain why the vehicle keeps moving after a switch.',
    'Compare switching counts with and without hysteresis.',
  ],
  controls: ['controller-kind', 'bang', 'feedforward'],
  controllerKinds: ['bang-bang'],
  sections: [
    {
      id: 'm3-rule',
      title: 'Two thrust levels',
      phase: 'learn',
      blocks: [
        {
          type: 'p',
          text: 'Before designing complex mathematical flight algorithms, control engineers often start with the simplest feedback strategy imaginable: on-off control, known in robotics as {{bang-bang}} control. Bang-bang logic is common in systems equipped with binary actuators, such as cold-gas reaction thrusters on satellites, solenoid valves, or simple thermostat heating elements. But applying hard on-off switching to electric drone motors reveals severe physical challenges: mechanical vibrations, electrical spikes, motor heating, and high-frequency chattering.',
        },
        {
          type: 'p',
          text: 'In our 1D multirotor, a bang-bang controller chooses between two discrete thrust levels placed symmetrically around baseline hover thrust $T_0$ using a thrust offset $\\Delta = 3\\text{ N}$:',
        },
        {
          type: 'list',
          items: [
            '**High level** ($T_{cmd} = T_0 + \\Delta = 12.81\\text{ N}$): Commanded when the drone is below target (positive error $e > 0$) to push upward.',
            '**Low level** ($T_{cmd} = T_0 - \\Delta = 6.81\\text{ N}$): Commanded when the drone is above target (negative error $e < 0$) to drop downward.',
          ],
        },
        {
          type: 'p',
          text: 'A naive sign rule flips states the instant error crosses zero ($e = 0$). Near the target altitude, tiny sensor fluctuations make the controller switch back and forth hundreds of times per second. This destructive high-frequency hammering is known as **chatter**.',
        },
        {
          type: 'p',
          text: 'To protect the hardware, engineers add {{hysteresis}}, a buffer band with half-width {{h}}. The controller only changes states when the error convincingly crosses outside the band; inside the band, it simply preserves its previous state:',
        },
        {
          type: 'math',
          tex: '\\text{level} = \\begin{cases} \\text{high} & \\term{error}{e} > \\term{h}{h} \\\\ \\text{low} & \\term{error}{e} < -\\term{h}{h} \\\\ \\text{previous level} & -\\term{h}{h} \\le \\term{error}{e} \\le \\term{h}{h} \\end{cases}',
          aria: 'Choose high if error e is greater than h. Choose low if e is less than minus h. Otherwise, including exactly at plus or minus h, keep the previous level.',
        },
        {
          type: 'p',
          text: 'When $h = 0$, the band vanishes and switching occurs at $e = 0$. In our simulation, the initial switch state starts at "low" until triggered.',
        },
        { type: 'widget', widget: 'bang-levels' },
      ],
    },
    {
      id: 'm3-predict',
      title: 'Predict',
      phase: 'predict',
      blocks: [
        {
          type: 'p',
          text: 'Consider the physical relationship between instantaneous force and momentum to predict what happens during a bang-bang switch.',
        },
        {
          type: 'predict',
          id: 'm3-p1',
          prompt: 'The vehicle climbs toward 2.5 m on the high level. At the instant it reaches 2.5 m the controller switches to low. What happens next?',
          options: [
            { id: 'stop', text: 'It stops exactly at 2.5 m.' },
            { id: 'over', text: 'It keeps rising for a while, then comes back down.' },
            { id: 'drop', text: 'It immediately starts falling.' },
          ],
          reveal: 'Switching thrust changes the acceleration instantaneously, but velocity cannot change instantly! Because the vehicle was actively climbing when it crossed 2.5 m, its upward momentum carries it higher before the downward net force can brake it to a halt. This overshoot is inevitable with binary switching.',
        },
      ],
    },
    {
      id: 'm3-lab',
      title: 'With and without hysteresis',
      phase: 'experiment',
      blocks: [
        {
          type: 'p',
          text: 'In this lab, you will explore the physical behavior of on-off (bang-bang) control and examine the critical trade-off introduced by a hysteresis switching window.',
        },
        {
          type: 'h',
          text: 'Part 1: Pure Bang-Bang Switching (h = 0)',
        },
        {
          type: 'p',
          text: '**Purpose**: Observe actuator chattering. With zero tolerance ($h = 0$), any infinitesimal crossing of the target threshold causes the controller to slam thrust between maximum high and minimum low. In real hardware, this extreme switching frequency overheats motor windings and destroys mechanical switches.',
        },
        {
          type: 'h',
          text: 'Part 2: Hysteresis Deadband (h = 0.05 m)',
        },
        {
          type: 'p',
          text: '**Purpose**: Trade absolute accuracy for actuator longevity. Adding a 5 cm hysteresis band creates an intentional dead zone: the controller only flips state when the vehicle fully crosses beyond $\\pm h$. This drastically lowers motor switching frequency while trapping the vehicle in a steady, periodic limit-cycle oscillation.',
        },
        { type: 'lab' },
      ],
    },
    {
      id: 'm3-inspect',
      title: 'Inspect',
      phase: 'inspect',
      blocks: [
        {
          type: 'p',
          text: 'Examine the telemetry logs to analyze the trade-offs of bang-bang switching:',
        },
        {
          type: 'list',
          items: [
            'Compare both runs: notice how the total switch count drops dramatically when $h = 0.05\\text{ m}$. Where are the high-frequency switches concentrated when $h = 0$?',
            'Measure the first peak above 2.5 m. How far beyond the target line did momentum carry the vehicle? That maximum deviation is the {{overshoot}}.',
            'Observe that even with hysteresis, the vehicle never settles into a smooth hover; it enters a permanent, oscillating {{limit-cycle}}.',
          ],
        },
      ],
    },
    {
      id: 'm3-explain',
      title: 'Explain',
      phase: 'explain',
      blocks: [
        { type: 'reflect', id: 'm3-r1', prompt: 'Why might rapid switching be bad for real motors, and what did hysteresis trade to reduce it?' },
      ],
    },
  ],
  lab: {
    id: 'm3-lab',
    version: 1,
    instructions: 'Complete both 10 s runs to evaluate bang-bang control: Step 1 tests zero deadband (h = 0); Step 2 tests a 5 cm hysteresis band (h = 0.05 m).',
    steps: [
      {
        id: 'm3-h0',
        title: 'Part 1: No hysteresis ($h = 0$)',
        presetId: 'bang-bang',
        instructions:
          'Load the step and run to the end (10 s). Watch the thrust trace chatter and flicker wildly between 12.81 N and 6.81 N as the drone reaches 2.5 m, racking up a massive switch count in the metrics panel.',
        permitted: [],
        requires: 'A complete 10 s run with h = 0.',
      },
      {
        id: 'm3-h05',
        title: 'Part 2: Hysteresis $h = 0.05$ m',
        presetId: 'bang-bang',
        setup: (c) => {
          c.controller.hysteresis = H;
        },
        instructions:
          'Load the step ($h = 0.05\\text{ m}$) and run to the end (10 s). Observe how the 5 cm deadband dramatically reduces motor switching, trading pinpoint precision for a steady, bounded limit-cycle oscillation around 2.5 m.',
        permitted: [],
        requires: 'A complete 10 s run with h = 0.05 m.',
      },
    ],
  },
  gate: {
    id: 'g3',
    version: 1,
    title: 'Check: switching and overshoot',
    questions: [
      {
        id: 'g3-q1',
        version: 1,
        kind: 'choice',
        binds: [bandRun],
        prompt: (ctx) => {
          const { prev, cur } = insideBand(ctx);
          return `From your h = 0.05 m run at t = ${f(cur.t)} s: the previous level was ${level(prev.sw)} and the error is e = ${f(cur.e, 3)} m. Which level does the controller select next?`;
        },
        options: [
          { id: 'high', text: 'High ($T_0$ + 3 N)' },
          { id: 'low', text: 'Low ($T_0$ − 3 N)' },
        ],
        correct: (ctx) => {
          const { prev, cur } = insideBand(ctx);
          return nextLevel(prev.sw, cur.e, H);
        },
        hint: 'Is |e| bigger than h? If not, the controller keeps its previous level.',
        explanation: 'Inside the band, hysteresis retains the previous level.',
        review: 'm3-rule',
      },
      {
        id: 'g3-q2',
        version: 1,
        kind: 'choice',
        binds: [bandRun],
        prompt: (ctx) => {
          const { prev, cur } = belowBand(ctx);
          return `Another moment (t = ${f(cur.t)} s): previous level ${level(prev.sw)}, error e = ${f(cur.e, 3)} m, h = 0.05 m. Which level next?`;
        },
        options: [
          { id: 'high', text: 'High' },
          { id: 'low', text: 'Low' },
        ],
        correct: (ctx) => {
          const { prev, cur } = belowBand(ctx);
          return nextLevel(prev.sw, cur.e, H);
        },
        hint: 'Negative error means the vehicle is above the target.',
        explanation: 'When e < −h the vehicle is clearly too high, so the controller switches to the low level.',
        review: 'm3-rule',
      },
      {
        id: 'g3-q3',
        version: 1,
        kind: 'numeric',
        binds: [bandRun],
        prompt: () => 'In your h = 0.05 m run, how far above the 2.5 m target did the altitude peak? Report peak altitude minus 2.5 m.',
        table: (ctx) => {
          const p = peak(ctx, bandRun);
          return { caption: 'Peak of your run', headers: ['t (s)', 'z (m)'], rows: [[f(p.t), f(p.z, 3)]] };
        },
        unit: 'm',
        answer: (ctx) => peak(ctx, bandRun).z - 2.5,
        tolerance: 0.003,
        hint: 'Subtract the target from the peak altitude.',
        explanation: 'That distance past the target is the overshoot. Divide by the 0.5 m step to express it as a fraction.',
        review: 'm3-inspect',
      },
      {
        id: 'g3-q4',
        version: 1,
        kind: 'choice',
        binds: [],
        prompt: () => 'Why does the vehicle keep rising for a while after the controller switches to the low level?',
        options: [
          { id: 'inertia', text: 'Thrust sets acceleration. The vehicle still has upward velocity that takes time to remove.' },
          { id: 'delay', text: 'The sensor reports altitude late.', feedback: 'Sensing here is instantaneous. The cause is the vehicle’s own motion.' },
          { id: 'weak', text: 'The low level is still above the weight.', feedback: 'The low level is $T_0$ − 3 N, below the weight. The net force points down immediately.' },
        ],
        correct: () => 'inertia',
        hint: 'What does a force change directly: position, velocity, or acceleration?',
        explanation: 'Mass carries momentum past the target. That is inertia.',
        review: 'm3-predict',
      },
      {
        id: 'g3-q5',
        version: 1,
        kind: 'choice',
        binds: ['m3-h0', bandRun],
        prompt: (ctx) =>
          `Your runs switched ${run(ctx, 'm3-h0').stats.switchCount} times with h = 0 and ${run(ctx, bandRun).stats.switchCount} times with h = 0.05 m. Which claim do they support?`,
        options: [
          { id: 'fewer', text: 'Hysteresis reduced switching, but the vehicle still oscillated around the target.' },
          { id: 'converge', text: 'Hysteresis made the vehicle settle exactly on the target.', feedback: 'Look at the altitude plot. It keeps crossing the target band.' },
          { id: 'more', text: 'Hysteresis increased switching.', feedback: 'Compare the two counts again.' },
        ],
        correct: (ctx) => (run(ctx, bandRun).stats.switchCount < run(ctx, 'm3-h0').stats.switchCount ? 'fewer' : 'more'),
        hint: 'Use the switch counts and the altitude plots.',
        explanation: 'Hysteresis trades a bigger wobble for far fewer switches. It does not guarantee convergence.',
        review: 'm3-inspect',
      },
    ],
  },
};
